import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Pagination } from 'swiper/modules';
import { Sparkles, Play, MapPin } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const slides = [
  {
    tagline: 'Find the Perfect Party',
    description: 'Discover wild club nights, laid-back lounges, and themed events',
    color: '#ff84e4',
    bgGradient: 'from-[#2d1b4e] via-[#1a0a2e] to-[#0a0a0b]',
  },
  {
    tagline: 'Embrace the Adventure',
    description: 'Experience the thrill of discovering hidden gems in your city',
    color: '#d987ff',
    bgGradient: 'from-[#1b3b4e] via-[#0a1a2e] to-[#0a0a0b]',
  },
  {
    tagline: 'Music Lovers Rejoice',
    description: 'Find venues that play your favorite beats all night long',
    color: '#00f5d4',
    bgGradient: 'from-[#1b4e3d] via-[#0a2e1a] to-[#0a0a0b]',
  },
  {
    tagline: 'Exclusive VIP Access',
    description: 'Unlock special perks, discounts, and offers at select venues',
    color: '#ffe03d',
    bgGradient: 'from-[#4e3b1b] via-[#2e1a0a] to-[#0a0a0b]',
  },
];

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Swiper */}
      <Swiper
        modules={[EffectFade, Autoplay, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop={true}
        speed={1000}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="absolute inset-0 h-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className={`absolute inset-0 bg-gradient-to-br ${slide.bgGradient}`} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-16 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-left"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full mb-6"
              >
                <Sparkles size={16} className="text-[#ff84e4]" />
                <span className="font-mono text-xs uppercase tracking-wider text-white/80">
                  Party & Nightlife Discovery
                </span>
              </motion.div>

              {/* Main Heading */}
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4">
                Discover Local
                <br />
                <span
                  className="transition-colors duration-500"
                  style={{ color: slides[activeIndex].color }}
                >
                  Travel Global!
                </span>
              </h1>

              {/* Dynamic Tagline */}
              <motion.p
                key={activeIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-xl md:text-2xl text-white/60 mb-8 max-w-lg"
              >
                {slides[activeIndex].description}
              </motion.p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <motion.a
                  href="https://play.google.com/store/apps/details?id=com.pantech.xtraglass"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-8 py-4 bg-[#ff84e4] text-black font-mono text-sm uppercase tracking-wider rounded-full hover:shadow-[0_0_30px_rgba(255,132,228,0.4)] transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  Google Play
                </motion.a>

                <motion.a
                  href="#demo"
                  className="flex items-center gap-3 px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-mono text-sm uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Play size={18} fill="currentColor" />
                  Watch Demo
                </motion.a>
              </div>

              {/* Stats */}
              <div className="flex gap-8 mt-10 pt-8 border-t border-white/10">
                <div>
                  <p className="font-display text-3xl font-bold text-white">50+</p>
                  <p className="font-mono text-xs uppercase tracking-wider text-white/50">Cities</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-bold text-white">1000+</p>
                  <p className="font-mono text-xs uppercase tracking-wider text-white/50">Venues</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-bold text-white">24/7</p>
                  <p className="font-mono text-xs uppercase tracking-wider text-white/50">Updates</p>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative flex justify-center lg:justify-end"
            >
              <div className="relative">
                {/* Glow Effect */}
                <div
                  className="absolute inset-0 blur-3xl opacity-30 transition-colors duration-500"
                  style={{ backgroundColor: slides[activeIndex].color }}
                />
                
                {/* Phone Image */}
                <motion.img
                  src="/images/iphone63.png"
                  alt="XtraGlass App"
                  className="relative w-72 md:w-80 lg:w-96 drop-shadow-2xl"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Floating Cards */}
                <motion.div
                  className="absolute -left-8 top-1/4 glass px-4 py-3 rounded-xl"
                  animate={{ y: [0, -10, 0], x: [0, 5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-[#ff84e4]" />
                    <span className="font-mono text-xs text-white/80">Near You</span>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute -right-4 bottom-1/3 glass px-4 py-3 rounded-xl"
                  animate={{ y: [0, 10, 0], x: [0, -5, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-[#d987ff]" />
                    <span className="font-mono text-xs text-white/80">VIP Access</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <motion.div className="w-1.5 h-1.5 bg-[#ff84e4] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;